import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaExclamationTriangle, FaList, FaBars, FaTimes, FaExchangeAlt, FaCheckCircle } from 'react-icons/fa';
import HolyLogo from '../components/HolyLogo';

const NavContainer = styled.nav`
  background-color: rgba(26, 26, 26, 0.85);
  box-shadow: ${props => props.theme.boxShadow.small};
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(15px);
  transition: ${props => props.theme.transition.default};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &.scrolled {
    padding: 0.8rem 2rem;
    box-shadow: ${props => props.theme.boxShadow.medium};
    background-color: rgba(20, 20, 20, 0.95);
  }
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: ${props => props.theme.fonts.headings};
  background: ${props => props.theme.colors.gradient1};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: ${props => props.theme.transition.default};
  
  svg {
    margin-right: 0.5rem;
    font-size: 1.8rem;
    color: ${props => props.theme.colors.primary};
    transition: ${props => props.theme.transition.default};
  }
  
  &:hover {
    svg {
      animation: ${LogoAnimation} 1s ease infinite;
      color: ${props => props.theme.colors.primaryLight};
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    width: 250px;
    height: 100vh;
    background: ${props => props.theme.colors.darkBg2};
    flex-direction: column;
    padding: 2rem;
    gap: 1rem;
    box-shadow: ${props => props.isOpen ? '-5px 0 15px rgba(0, 0, 0, 0.5)' : 'none'};
    transition: ${props => props.theme.transition.default};
    z-index: 1000;
  }
`;

const LinkUnderline = keyframes`
  0% {
    width: 0;
    left: 50%;
  }
  100% {
    width: 80%;
    left: 10%;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.text};
  font-weight: ${props => props.isActive ? '600' : '500'};
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  transition: ${props => props.theme.transition.default};
  position: relative;
  overflow: hidden;
  
  ${props => props.isActive && css`
    background-color: rgba(108, 92, 231, 0.1);
  `}
  
  &:hover {
    background-color: rgba(108, 92, 231, 0.1);
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    
    &::after {
      animation: ${LinkUnderline} 0.3s forwards;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 6px;
    left: 50%;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.gradient1};
    border-radius: 2px;
    transition: ${props => props.theme.transition.fast};
  }
  
  svg {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    padding: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: ${props => props.theme.colors.primary};
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    // Close mobile menu when location changes
    setMobileMenuOpen(false);
  }, [location]);
  
  return (
    <>
      <NavContainer className={scrolled ? 'scrolled' : ''}>
        <NavContent>
          <Logo to="/">
            <HolyLogo style={{ fontSize: '2rem', marginRight: '-0.50rem' }} />
            olyHubs
          </Logo>
          
          <NavLinks isOpen={mobileMenuOpen}>
            <MobileMenuCloseButton onClick={toggleMobileMenu}>
              <FaTimes />
            </MobileMenuCloseButton>
            
            <NavLink to="/escrow" isActive={location.pathname === '/escrow'}>
              <FaExchangeAlt />
              Escrow
            </NavLink>
            <NavLink to="/scam-list" isActive={location.pathname === '/scam-list'}>
              <FaList />
              Scamlist
            </NavLink>
            <NavLink to="/report" isActive={location.pathname === '/report'}>
              <FaExclamationTriangle />
              Signaler
            </NavLink>
            <NavLink to="/certified-sellers" isActive={location.pathname === '/certified-sellers'}>
              <FaCheckCircle />
              Vendeurs Certifiés
            </NavLink>
          </NavLinks>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            <FaBars />
          </MobileMenuButton>
        </NavContent>
      </NavContainer>
      
      <Overlay isOpen={mobileMenuOpen} onClick={toggleMobileMenu} />
    </>
  );
};

export default Navbar; 